import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { approveDraft, getLeadsByIds, getMessagesByIds, listDrafts, rejectDraft } from '../lib/api';
import { dateTime, formatPhone, friendlyError } from '../lib/format';
import type { AiDraft, Lead, Message } from '../lib/types';
import { Badge, Button, Empty, ErrorText, PageHeader } from '../components/ui';

type Tab = 'held' | 'blocked' | 'done';
const TAB_STATUSES: Record<Tab, AiDraft['status'][]> = {
  held: ['held', 'stale'], blocked: ['blocked', 'failed'], done: ['approved', 'rejected'],
};

export function Review() {
  const { members } = useAuth();
  const [tab, setTab] = useState<Tab>('held');
  const [drafts, setDrafts] = useState<AiDraft[]>([]);
  const [leads, setLeads] = useState<Record<string, Lead>>({});
  const [ins, setIns] = useState<Record<number, Message>>({});
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const ds = await listDrafts(TAB_STATUSES[tab]);
      const [ls, ms] = await Promise.all([getLeadsByIds([...new Set(ds.map((d) => d.lead_id))]), getMessagesByIds(ds.map((d) => d.in_message_id))]);
      setDrafts(ds);
      setLeads(Object.fromEntries(ls.map((l) => [l.id, l])));
      setIns(Object.fromEntries(ms.map((m) => [m.id, m])));
    } catch (e) { setError(friendlyError(e)); }
  }, [tab]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const nameOf = (id: string | null) => members.find((m) => m.id === id)?.full_name ?? 'Unassigned';

  return (
    <div>
      <PageHeader title="Review" subtitle="AI replies the gate stopped. The buyer already has a holding message, so answer within minutes." />
      <div className="mb-4 flex gap-1 rounded-lg bg-surface2 p-1 text-sm font-medium sm:w-fit">
        {(['held', 'blocked', 'done'] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`flex-1 rounded-md px-4 py-1.5 ${tab === t ? 'bg-surface shadow' : 'text-muted'}`}>
            {t === 'held' ? 'Waiting' : t === 'blocked' ? 'Blocked' : 'Done'}
          </button>
        ))}
      </div>
      <div className="mb-3"><ErrorText>{error}</ErrorText></div>
      {drafts.length === 0 && <Empty title={tab === 'held' ? 'Nothing waiting' : 'Nothing here'}>{tab === 'held' ? 'Replies with numbers that are not in the approved facts land here.' : undefined}</Empty>}
      <ul className="flex flex-col gap-3">
        {drafts.map((d) => <DraftCard key={d.id} draft={d} lead={leads[d.lead_id]} inMsg={ins[d.in_message_id]} nameOf={nameOf} onDone={load} />)}
      </ul>
    </div>
  );
}

function DraftCard({ draft, lead, inMsg, nameOf, onDone }: { draft: AiDraft; lead?: Lead; inMsg?: Message; nameOf: (id: string | null) => string; onDone: () => void }) {
  const [text, setText] = useState(draft.draft ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const actionable = draft.status === 'held';
  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError('');
    try { await fn(); onDone(); } catch (e) { setError(friendlyError(e)); setBusy(false); }
  }
  const tone = draft.status === 'blocked' || draft.status === 'failed' ? 'bad' : draft.status === 'stale' ? 'muted' : draft.status === 'held' ? 'warn' : 'ok';
  const label: Record<AiDraft['status'], string> = { held: 'Waiting', stale: 'Stale: price sheet changed', blocked: 'Blocked', failed: 'AI could not reply', approved: 'Approved', rejected: 'Rejected', sent: 'Sent' };
  return (
    <li className="rounded-2xl border border-line bg-surface p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={tone}>{label[draft.status]}</Badge>
          {lead?.is_test && <Badge tone="warn">Test</Badge>}
          {lead && <Link to={`/leads?lead=${lead.id}`} className="font-medium hover:text-accent">{lead.buyer?.name || formatPhone(lead.buyer?.phone_e164 ?? '')} · L-{lead.ref_no}</Link>}
          {lead && <span className="text-sm text-muted">{nameOf(lead.owner_id)}</span>}
        </div>
        <span className="text-xs text-muted">{dateTime(draft.created_at)}</span>
      </div>
      {inMsg && <p className="mb-2 rounded-xl bg-surface2 px-3 py-2 text-sm"><span className="text-muted">Buyer: </span>{inMsg.body}</p>}
      {draft.reasons.length > 0 && <p className={`mb-2 text-sm ${tone === 'bad' ? 'text-bad' : 'text-warn'}`}>{draft.reasons.join(' · ')}</p>}
      {actionable ? (
        <>
          <textarea aria-label="Reply to send" rows={3} value={text} onChange={(e) => setText(e.target.value)}
            className="mb-2 w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-accent" />
          <p className="mb-2 text-xs text-muted">Check every number against the builder's sheet before sending. You are approving it.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" disabled={busy} onClick={() => run(() => approveDraft(draft.id, text, !!lead?.is_test))}>Approve and send</Button>
            <Button variant="ghost" disabled={busy} onClick={() => run(() => rejectDraft(draft.id, ''))}>Reject</Button>
          </div>
        </>
      ) : (
        draft.draft && <p className="whitespace-pre-wrap text-sm"><span className="text-muted">AI wrote: </span>{draft.final_text ?? draft.draft}</p>
      )}
      {draft.status === 'stale' && <div className="mt-2"><Button variant="ghost" disabled={busy} onClick={() => run(() => rejectDraft(draft.id, 'Price sheet changed'))}>Clear it</Button></div>}
      <div className="mt-2"><ErrorText>{error}</ErrorText></div>
    </li>
  );
}
